import BaseService from '@/services/BaseService';
import { ResponseError } from '@/lib/exceptions';

export type TripleNetLeaseDashboardFilterOptions = {
  dealNames: string[];
  dealerGroups: string[];
  statesOrTerritories: string[];
};

export type TripleNetLeaseTapeDashboardKpiDealRow = {
  dealName: string;
  totalAppraisedValue: number;
  sumAdvanceAmount: number;
  sumFccrTimesAdvance: number;
  sumDscrTimesAdvance: number;
  sumInPoolAdvanceAmount: number;
};

export type TripleNetLeaseTapeDashboardKpiLatest = {
  asOfDateIso: string;
  totalAppraisedValue: number;
  waLtvPct: number | null;
  waFccr: number | null;
  waDscr: number | null;
  /** Deal-grain weight components for Pro Rata roll-up. */
  byDeal: TripleNetLeaseTapeDashboardKpiDealRow[];
};

export type TripleNetLeaseAdvanceWeightedBucketRow = {
  bucketLabel: string;
  advanceAmount: number;
};

export type TripleNetLeaseAdvanceWeightedBucketByDealRow = {
  bucketLabel: string;
  dealName: string;
  advanceAmount: number;
};

export type TripleNetLeaseWaLtvLeaseTermTrendRow = {
  asOfDateIso: string;
  dealName: string;
  sumAdvanceAmount: number;
  sumAppraisedValue: number;
  sumRemainingTermYearsTimesAdvance: number;
  /**
   * When set, the API already returned portfolio WA metrics (legacy shape).
   * Roll-up uses these values as-is instead of recomputing from weights.
   */
  preAggregated?: {
    waLtvPct: number | null;
    waLeaseTermYrs: number | null;
  };
};

export type TripleNetLeaseOffPoolPropertyMixRow = {
  /** Empty when the API returns portfolio property mix without deal breakout. */
  dealName: string;
  propertyType: string;
  propertyCount: number;
};

const FILTER_OPTIONS_PATH = 'triple-net-lease-dashboard/filter-options';

function stringArrayFromBody(body: Record<string, unknown>, camelKey: string, pascalKey: string): string[] {
  const raw = body[camelKey] ?? body[pascalKey];
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === 'string');
}

function parseNum(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseKpiDealRows(raw: unknown): TripleNetLeaseTapeDashboardKpiDealRow[] {
  if (!Array.isArray(raw)) return [];
  const out: TripleNetLeaseTapeDashboardKpiDealRow[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const dealName = r.dealName ?? r.DealName;
    if (typeof dealName !== 'string' || dealName.length === 0) continue;
    const totalAppraisedValue = parseNum(r.totalAppraisedValue ?? r.TotalAppraisedValue) ?? 0;
    const sumAdvanceAmount = parseNum(r.sumAdvanceAmount ?? r.SumAdvanceAmount) ?? 0;
    const sumFccrTimesAdvance = parseNum(r.sumFccrTimesAdvance ?? r.SumFccrTimesAdvance) ?? 0;
    const sumDscrTimesAdvance = parseNum(r.sumDscrTimesAdvance ?? r.SumDscrTimesAdvance) ?? 0;
    const sumInPoolAdvanceAmount = parseNum(r.sumInPoolAdvanceAmount ?? r.SumInPoolAdvanceAmount) ?? 0;
    out.push({
      dealName,
      totalAppraisedValue,
      sumAdvanceAmount,
      sumFccrTimesAdvance,
      sumDscrTimesAdvance,
      sumInPoolAdvanceAmount,
    });
  }
  return out;
}

function parseTripleNetLeaseKpiRow(raw: unknown): TripleNetLeaseTapeDashboardKpiLatest | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const asOf = o.asOfDateIso ?? o.AsOfDateIso;
  if (typeof asOf !== 'string' || asOf.length === 0) return null;
  const totalAppraisedValue = parseNum(o.totalAppraisedValue ?? o.TotalAppraisedValue);
  if (totalAppraisedValue == null) return null;
  const waLtvPct = parseNum(o.waLtvPct ?? o.WaLtvPct);
  const waFccr = parseNum(o.waFccr ?? o.WaFccr);
  const waDscr = parseNum(o.waDscr ?? o.WaDscr);
  const byDeal = parseKpiDealRows(o.byDeal ?? o.ByDeal);

  return {
    asOfDateIso: asOf,
    totalAppraisedValue,
    waLtvPct,
    waFccr,
    waDscr,
    byDeal,
  };
}

function parseAdvanceWeightedBucketRows(raw: unknown): TripleNetLeaseAdvanceWeightedBucketRow[] {
  if (raw == null || typeof raw !== 'object') return [];
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) return [];
  const out: TripleNetLeaseAdvanceWeightedBucketRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const bucket = r.bucketLabel ?? r.BucketLabel;
    if (typeof bucket !== 'string' || bucket.length === 0) continue;
    const amount = parseNum(r.advanceAmount ?? r.AdvanceAmount);
    if (amount == null) continue;
    out.push({ bucketLabel: bucket, advanceAmount: amount });
  }
  return out;
}

function parseAdvanceWeightedBucketByDealRows(raw: unknown): TripleNetLeaseAdvanceWeightedBucketByDealRow[] {
  if (raw == null || typeof raw !== 'object') return [];
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) return [];
  const out: TripleNetLeaseAdvanceWeightedBucketByDealRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const bucket = r.bucketLabel ?? r.BucketLabel;
    const dealName = r.dealName ?? r.DealName;
    if (typeof bucket !== 'string' || bucket.length === 0) continue;
    if (typeof dealName !== 'string' || dealName.length === 0) continue;
    const amount = parseNum(r.advanceAmount ?? r.AdvanceAmount);
    if (amount == null) continue;
    out.push({ bucketLabel: bucket, dealName, advanceAmount: amount });
  }
  return out;
}

function parseWaLtvLeaseTermTrendRows(raw: unknown): TripleNetLeaseWaLtvLeaseTermTrendRow[] {
  if (raw == null || typeof raw !== 'object') return [];
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) return [];
  const out: TripleNetLeaseWaLtvLeaseTermTrendRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const asOf = r.asOfDateIso ?? r.AsOfDateIso;
    if (typeof asOf !== 'string' || asOf.length === 0) continue;

    const dealNameRaw = r.dealName ?? r.DealName;
    const dealName = typeof dealNameRaw === 'string' ? dealNameRaw.trim() : '';
    const sumAdvance = parseNum(r.sumAdvanceAmount ?? r.SumAdvanceAmount);
    const sumAppraised = parseNum(r.sumAppraisedValue ?? r.SumAppraisedValue);
    const sumTermTimesAdvance = parseNum(r.sumRemainingTermYearsTimesAdvance ?? r.SumRemainingTermYearsTimesAdvance);

    // Deal-grain shape (current API): scale at deal, then roll up on the client.
    if (dealName && (sumAdvance != null || sumAppraised != null || sumTermTimesAdvance != null)) {
      out.push({
        asOfDateIso: asOf,
        dealName,
        sumAdvanceAmount: sumAdvance ?? 0,
        sumAppraisedValue: sumAppraised ?? 0,
        sumRemainingTermYearsTimesAdvance: sumTermTimesAdvance ?? 0,
      });
      continue;
    }

    // Legacy portfolio shape: already rolled up WA metrics (no deal breakout).
    const waLtvPct = parseNum(r.waLtvPct ?? r.WaLtvPct);
    const waLeaseTermYrs = parseNum(r.waLeaseTermYrs ?? r.WaLeaseTermYrs);
    if (waLtvPct == null && waLeaseTermYrs == null) continue;
    out.push({
      asOfDateIso: asOf,
      dealName: '',
      sumAdvanceAmount: 0,
      sumAppraisedValue: 0,
      sumRemainingTermYearsTimesAdvance: 0,
      preAggregated: { waLtvPct, waLeaseTermYrs },
    });
  }
  return out;
}

function parseOffPoolPropertyMixRows(raw: unknown): TripleNetLeaseOffPoolPropertyMixRow[] {
  if (raw == null || typeof raw !== 'object') return [];
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) return [];
  const out: TripleNetLeaseOffPoolPropertyMixRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const dealNameRaw = r.dealName ?? r.DealName;
    const propertyType = r.propertyType ?? r.PropertyType;
    if (typeof propertyType !== 'string' || propertyType.length === 0) continue;
    const propertyCount = parseNum(r.propertyCount ?? r.PropertyCount);
    if (propertyCount == null) continue;
    out.push({
      dealName: typeof dealNameRaw === 'string' ? dealNameRaw.trim() : '',
      propertyType,
      propertyCount: Math.trunc(propertyCount),
    });
  }
  return out;
}

/**
 * Snapshot KPIs for SP_TRIPLE_NET_LEASE_TAPE, optionally filtered by deal name,
 * dealer group, state/territory, and as-of date.
 */

export type TripleNetLeaseDashboardFilters = {
  dealName: readonly string[];
  dealerGroup: readonly string[];
  stateOrTerritory: readonly string[];
  /** Selected tape AS_OF_DATE (YYYY-MM-DD). Empty = latest. */
  asOfDate?: string;
};

function appendTripleNetLeaseFilterParams(qs: URLSearchParams, filters: TripleNetLeaseDashboardFilters) {
  for (const v of filters.dealName) {
    const t = v.trim();
    if (t) qs.append('dealName', t);
  }
  for (const v of filters.dealerGroup) {
    const t = v.trim();
    if (t) qs.append('dealerGroup', t);
  }
  for (const v of filters.stateOrTerritory) {
    const t = v.trim();
    if (t) qs.append('stateOrTerritory', t);
  }
  const asOf = filters.asOfDate?.trim();
  if (asOf) qs.set('asOfDate', asOf);
}

export class TripleNetLeaseDashboardService extends BaseService {
  constructor() {
    super('/api/digitalconnect-service');
  }

  getJsonBody = async (path: string) => {
    const raw = await this.get({ path: path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      return (await raw.json()) as Record<string, unknown>;
    }
    if (raw && typeof raw === 'object') return raw as Record<string, unknown>;
    return {};
  };

  fetchTripleNetLeaseDashboardFilterOptions = async () => {
    const body = await this.getJsonBody(FILTER_OPTIONS_PATH);
    return {
      dealNames: stringArrayFromBody(body, 'dealNames', 'DealNames'),
      dealerGroups: stringArrayFromBody(body, 'dealerGroups', 'DealerGroups'),
      statesOrTerritories: stringArrayFromBody(body, 'statesOrTerritories', 'StatesOrTerritories'),
    };
  };

  fetchTripleNetLeaseDashboardAsOfDates = async () => {
    const body = await this.getJsonBody('triple-net-lease-dashboard/as-of-dates');
    return stringArrayFromBody(body, 'asOfDates', 'AsOfDates');
  };

  fetchTripleNetLeaseTapeDashboardKpi = async (filters: TripleNetLeaseDashboardFilters) => {
    const qs = new URLSearchParams();
    appendTripleNetLeaseFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `triple-net-lease-dashboard/tape-kpi?${q}` : 'triple-net-lease-dashboard/tape-kpi';

    const raw = await this.get({ path: path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) return null;
      const parsed: unknown = JSON.parse(text) as unknown;
      return parseTripleNetLeaseKpiRow(parsed);
    }
    return parseTripleNetLeaseKpiRow(raw);
  };

  fetchTripleNetLeaseRows = async <T>(
    pathBase: string,
    filters: TripleNetLeaseDashboardFilters,
    parser: (raw: unknown) => T,
  ): Promise<T> => {
    const qs = new URLSearchParams();
    appendTripleNetLeaseFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `${pathBase}?${q}` : pathBase;
    const raw = await this.get({ path: path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) return parser({});
      const parsed: unknown = JSON.parse(text) as unknown;
      return parser(parsed);
    }
    return parser(raw);
  };

  fetchTripleNetLeaseFccrDistribution = async (filters: TripleNetLeaseDashboardFilters) => {
    return this.fetchTripleNetLeaseRows(
      'triple-net-lease-dashboard/fccr-distribution',
      filters,
      parseAdvanceWeightedBucketByDealRows,
    );
  };

  fetchTripleNetLeaseDscrDistribution = async (filters: TripleNetLeaseDashboardFilters) => {
    return this.fetchTripleNetLeaseRows(
      'triple-net-lease-dashboard/dscr-distribution',
      filters,
      parseAdvanceWeightedBucketByDealRows,
    );
  };

  fetchTripleNetLeaseLeaseMaturityLadder = async (filters: TripleNetLeaseDashboardFilters) => {
    return this.fetchTripleNetLeaseRows(
      'triple-net-lease-dashboard/lease-maturity-ladder',
      filters,
      parseAdvanceWeightedBucketByDealRows,
    );
  };

  fetchTripleNetLeaseWaLtvLeaseTermTrend = async (filters: TripleNetLeaseDashboardFilters) => {
    return this.fetchTripleNetLeaseRows(
      'triple-net-lease-dashboard/wa-ltv-lease-term-trend',
      filters,
      parseWaLtvLeaseTermTrendRows,
    );
  };

  fetchTripleNetLeaseOffPoolPropertyMix = async (filters: TripleNetLeaseDashboardFilters) => {
    return this.fetchTripleNetLeaseRows(
      'triple-net-lease-dashboard/off-pool-property-mix',
      filters,
      parseOffPoolPropertyMixRows,
    );
  };
}
